import { Type } from '@angular/core';
import { ActivatedRouteSnapshot, Params } from '@angular/router';

import { ColumnLayoutService, LayoutColumn } from '@frontend/sports/layout/feature';
import { Observable, map, of, switchMap, tap } from 'rxjs';

export interface MasterDetailComponent<TMaster> {
    component: Type<any>;
    map: (data?: TMaster) => any;
}

export interface MasterDetailState<TMaster, TDetail> {
    master?: TMaster;
    detail?: TDetail;
}

export abstract class MasterDetailDependencyService<TMaster, TDetail> {
    private state?: MasterDetailState<TMaster, TDetail>;
    private redirect?: boolean;

    constructor(protected layoutService: ColumnLayoutService) {}

    abstract getData(route: ActivatedRouteSnapshot, detail?: TDetail): Observable<TMaster | undefined>;
    abstract getPreResolvedData(route: ActivatedRouteSnapshot): Observable<TMaster | undefined>;
    abstract getDetailData(route: ActivatedRouteSnapshot, master?: TMaster): Observable<TDetail | undefined>;
    abstract getPreResolvedDetailData(route: ActivatedRouteSnapshot): Observable<TDetail | undefined>;
    abstract getComponents(): MasterDetailComponent<TMaster>[];

    abstract getRouteParams?(): Params;

    updateState(state: MasterDetailState<TMaster, TDetail>): void {}

    shouldUpdateMasterCache(master: TMaster, route: ActivatedRouteSnapshot): boolean {
        return false;
    }

    shouldUpdateDetailCache(detail: TDetail, route: ActivatedRouteSnapshot): boolean {
        return true;
    }

    getContext(route: ActivatedRouteSnapshot, detail?: TDetail): Observable<TMaster | undefined> {
        if (this.state && this.state.master && detail && !this.shouldUpdateMasterCache(this.state.master, route)) {
            this.updateState(this.toState(this.state.master, detail));

            return of(this.state.master);
        }

        return this.getData(route, detail).pipe(
            switchMap((result) => {
                if (!detail && (this.redirect = this.hasDependency(route, result))) {
                    // we do not set the dependency here as the router will navigate to the fallback route and
                    // after the child route is loaded it will register the dependency
                    return this.getChildContext(route, result).pipe(map((data) => this.toState(result, data)));
                }

                return of(this.toState(result));
            }),
            tap((result) => {
                if (detail || result.detail) {
                    this.state = result;
                }
            }),
            map((result) => result.master),
        );
    }

    getCacheData(): TMaster | undefined {
        if (!this.state) {
            return;
        }

        return this.state.master;
    }

    getChildContext(route: ActivatedRouteSnapshot, master?: TMaster): Observable<TDetail | undefined> {
        if (this.state && this.state.detail && !this.shouldUpdateDetailCache(this.state.detail, route)) {
            this.registerDependency(this.state.master);

            return of(this.state.detail).pipe(tap(() => (this.state!.detail = undefined)));
        }

        return this.getDetailData(route, master).pipe(
            switchMap((result) => {
                if (!master && (this.redirect = this.hasDependency(route, result))) {
                    return this.getContext(route, result).pipe(
                        tap((data) => this.registerDependency(data)),
                        map((data) => this.toState(data, result)),
                    );
                }

                return of(this.toState(undefined, result));
            }),
            tap((result) => {
                if (master) {
                    this.state = result;
                }
            }),
            map((result) => result.detail),
        );
    }

    shouldRedirect(): boolean {
        return !!this.redirect;
    }

    deregister(): void {
        this.getComponents().forEach((registration) => this.layoutService.remove(registration.component, LayoutColumn.Left));
    }

    private toState(master?: TMaster, detail?: TDetail): MasterDetailState<TMaster, TDetail> {
        return {
            master,
            detail,
        };
    }

    protected hasDependency(route: ActivatedRouteSnapshot, result?: TMaster | TDetail): boolean {
        return this.layoutService.layout.shouldHaveLeftColumn && !!result;
    }

    registerDependency(state?: TMaster): void {
        if (!state) {
            this.deregister();

            return;
        }

        this.getComponents().forEach((registration) =>
            this.layoutService.register({
                component: registration.component,
                inputs: registration.map(state),
                location: LayoutColumn.Left,
            }),
        );
    }
}
